"use client"

import React, { useCallback, useEffect, useMemo, useState } from "react"

import { getSyncDictionary } from "@/lib/sync-dictionary"
import { cn, formatDate } from "@/lib/utils"
import {
  RemixIcon,
  TextInput,
  TTextInputProps,
  Typography,
} from "@/components/ui/atoms"

interface IExchangeRateResp {
  quotes: Record<string, number>
  source: string
}

interface ICurrencyConverterProps {
  isReverse: boolean
}
const CurrencyConverter = React.forwardRef<
  HTMLDivElement,
  ICurrencyConverterProps
>(({ isReverse, ...props }, ref) => {
  const [inputValue, setInputValue] = useState("")
  const [exRate, setExRate] = useState(0)
  const [isSwapped, setIsSwapped] = useState(isReverse)
  const [convertedValue, setConvertedValue] = useState(0)
  const dictionary = getSyncDictionary().page.simulateur
  const updateDate = useMemo(() => formatDate(new Date()), [])

  const EURInput = {
    id: "input",
    currencyValue: "EUR",
  } as TTextInputProps
  const CHFInput = {
    id: "input2",
    currencyValue: "CHF",
  } as TTextInputProps
  const fromCurrency = isSwapped
    ? CHFInput.currencyValue
    : EURInput.currencyValue
  const toCurrency = isSwapped ? EURInput.currencyValue : CHFInput.currencyValue

  const fetchExchangeRate = useCallback(async (): Promise<void> => {
    try {
      const response = await fetch(
        `/api/exchange-rate?currencyValueFrom=${fromCurrency}&currencyValueTo=${toCurrency}&ts=${Date.now()}`,
        { cache: "no-store" }
      )

      if (!response.ok) {
        throw new Error(`Error fetching exchange rate: ${response.status}`)
      }

      const data: IExchangeRateResp = await response.json()
      const key = `${fromCurrency}${toCurrency}`
      const rate = data.quotes[key]

      if (rate === undefined) {
        throw new Error(`Rate not found for ${key}`)
      }

      setExRate(rate)
    } catch (error) {
      console.error("Failed to fetch exchange rate:", error)
    }
  }, [fromCurrency, toCurrency])

  useEffect(() => {
    fetchExchangeRate()
  }, [fetchExchangeRate])

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value
    setInputValue(value)

    const numeric = Number(value)
    if (!isNaN(numeric) && value !== "") {
      setConvertedValue(Number((numeric * exRate).toFixed(4)))
    } else {
      setConvertedValue(0)
    }
  }

  const handleSwapClick = () => {
    setIsSwapped((prev) => !prev)
    setInputValue("")
    setConvertedValue(0)
  }

  return (
    <div className="container my-8 flex justify-center bg-neutral-200">
      <div
        className={
          "currency-converter flex w-fit flex-col items-center rounded-200 bg-neutral-100  p-7 shadow-200"
        }
        ref={ref}
        {...props}
      >
        <Typography
          as="h3"
          className="mb-9 text-center"
          variant="headingXSBold"
        >
          {isSwapped ? dictionary.titleChf : dictionary.titleEur}
        </Typography>
        <div className="relative flex flex-col items-end">
          <div
            className={cn(
              "flex-col content-center items-center rounded-200 xl:flex xl:flex-row"
            )}
          >
            <TextInput
              {...(isSwapped ? CHFInput : EURInput)}
              value={inputValue}
              onChange={handleInputChange}
              className="xl:mr-5"
            />
            <div className="flex justify-center">
              <button
                onClick={handleSwapClick}
                className="mx-2 my-5 cursor-pointer rounded-400 border-2 border-secondary-500 p-3 xl:my-0"
                aria-label="Inverser les devises"
              >
                <RemixIcon
                  name="ArrowLeftRight"
                  size="md"
                  className="hidden max-w-[3rem] text-secondary-500 xl:block"
                />
                <RemixIcon
                  name="ArrowUpDown"
                  size="md"
                  className="mx-auto max-w-[3rem] text-secondary-500 xl:hidden"
                />
              </button>
            </div>
            <TextInput
              className="xl:ml-5"
              {...(isSwapped ? EURInput : CHFInput)}
              value={convertedValue.toString()}
              disabled
            />
          </div>
          <div
            className={cn(
              "absolute bottom-0 flex translate-y-[2rem] items-center xl:translate-y-[1.25rem]"
            )}
          >
            <Typography
              variant="labelS"
              className="leading-[16px] text-neutral-400"
            >
              {dictionary.updatedOn} {updateDate}
            </Typography>
            <RemixIcon
              name="Information"
              size="sm"
              className="ml-2 text-neutral-400"
            />
          </div>
        </div>
        <Typography
          variant="labelS"
          className="mt-9 text-center leading-[16px] text-neutral-400"
        >
          <strong>
            1 {fromCurrency} = {exRate.toFixed(4)} {toCurrency}**
          </strong>
          <br />
          {dictionary.updatedOn} {updateDate}
          <br />
          {dictionary.legend}
        </Typography>
      </div>
    </div>
  )
})
CurrencyConverter.displayName = "CurrencyConverter"

export { CurrencyConverter, type IExchangeRateResp }
